import { handleRequest, API } from '../../services'
import Vue from 'vue'
import autoComplete from '../../components/Sheet/AutoComplete/state'
import router from '../../router/index'
import swal from 'sweetalert2'
import moment from 'moment'
import _ from 'lodash'
import { preLoadImages, groupBy } from '../../utils'

const state = {
  editingSheet: false,
  queueSheets: {actual: 0, total: 0, sheets: []},
  cachedSheets: {}, // key is the uuid and the value is the response of backend
  currentSheet: {},
  seendingSheet: false,
  shiftPressed: false,
  ctrlPressed: false,
  autoCompleting: false,
  selectedRowsAutoFill: [],
  journeys: [],
  journeySearch: '',
  selectedLines: [],
  processing: {
    djs: []  // indexes of lines
  }
}

const getters = {
  matchJourneySearch (state, getters) {
    if (!state.journeys.length || !state.journeySearch) { return null }
    let match = null
    for (let i in state.journeys) {
      let journey = state.journeys[i]
      if ((journey.code && journey.code.includes(state.journeySearch)) ||
        journey.name.toLowerCase().includes(state.journeySearch.toLowerCase())) {
        match = journey
        break
      }
    }
    return match
  },
  autoCompleting: state => state.autoCompleting,
  currentSheetId: state => {
    return state.currentSheet.id
  },
  currentSheetOrderedDigits: state => { return state.currentSheet.orderedDigis },
  currentSheetInfo: state => {
    return {
      employee: state.currentSheet.employee_name,
      start_date: state.currentSheet.start_date,
      imageUrl: state.currentSheet['image_url']
    }
  },
  digitsNeedVerify: state => {
    // for some reason this getter is been called before anithing else loads, so lets avoit if to break
    if (!state.currentSheet.rows) {
      return []
    }

    let digitsPosition = []
    state.currentSheet.rows.forEach((row, rowIndex) => {
      row.groups.forEach((group, gIndex) => {
        group.forEach((digit, dIndex) => {
          if (digit.verify) {
            digitsPosition.push([rowIndex, (dIndex + (gIndex * 4))])
          }
        })
      })
    })
    return digitsPosition
  },
  queueSheets: state => {
    return state.queueSheets
  },
  canEditCurrentSheet: state => {
    return !state.currentSheet.sent && !state.currentSheet.to_ignore
  },
  currentSheetRows: state => {
    return state.currentSheet.rows
  },
  currentSheetPdf: state => {
    return state.currentSheet.pdfUrl
  },
  currentDigit: state => {
    return state.currentSheet.currentDigit
  },
  editingSheet: state => {
    return state.editingSheet
  },
  seending: state => id => {
    return state.currentSheet.seending[id]
  },
  hasAnyPendingSeending: state => {
    let final = 0
    for (let key in state.currentSheet.seending) {
      if (state.currentSheet.seending[key]) {
        final++
      }
    }
    return final > 0
  },
  seendingSheet: state => {
    return state.seendingSheet
  },
  shiftPressed: state => state.shiftPressed,
  ctrlPressed: state => state.ctrlPressed,
  selectedRowsAutoFill: state => state.selectedRowsAutoFill
}

const mutations = {
  selectLineByIndex (state, payload) {
    if (!state.selectedLines.includes(payload)) {
      state.selectedLines.push(payload)
    } else {
      state.selectedLines = state.selectedLines.filter(i => i !== payload)
    }
  },
  selectedLines (state, payload) {
    state.selectedLines = payload
  },
  journeys: (state, payload) => {
    state.journeys = payload
  },
  journeySearch: (state, payload) => {
    state.journeySearch = payload
  },
  setCurrentDigitPosition: (state, payload) => {
    state.currentSheet.currentDigit = {
      id: state.currentSheet.orderedDigis[payload[0]][payload[1]],
      position: [payload[0], payload[1]]
    }
  },
  loadingSheet: (state, payload) => {
    state.currentSheet = {
      ...state.currentSheet,
      loading: true
    }
  },
  startVerify: (state, payload) => {
    state.currentSheet = {
      ...payload,
      orderedDigis: null,
      currentDigit: {
        id: payload.rows[0].groups[0][0].id,
        position: [0, 0]
      },
      seending: [],
      loading: false
    }

    // selected rows will be the onlyes that the machine_predicted gives less than 8 E's
    let toMark = []
    payload.rows.forEach((row, index) => {
      const allDigits = row.groups.reduce((a, b) => [...a, ...b])
      if (allDigits.filter(i => i.machine_predicted === 'E').length <= 8) {
        toMark.push(index)
      }
    })
    state.selectedRowsAutoFill = [...toMark]

    let orderedDigis = []
    state.currentSheet.rows.forEach((r) => {
      let row = []
      r.groups.forEach((group) => {
        group.forEach(digit => {
          row.push(digit.id)
        })
      })
      orderedDigis.push(row)
    })
    state.currentSheet.orderedDigis = orderedDigis
    state.editingSheet = true
  },
  autoFillRows (state, payload) {
    payload.forEach((row, i) => {
      Vue.set(state.currentSheet.rows, i, {...state.currentSheet.rows[i], ...row})
    })
  },
  selectedRowsAutoFill (state, payload) {
    if (state.selectedRowsAutoFill.includes(payload)) {
      state.selectedRowsAutoFill = state.selectedRowsAutoFill.filter(i => i !== payload)
    } else {
      state.selectedRowsAutoFill = [...state.selectedRowsAutoFill, payload]
    }
  },
  cutError: (state, payload) => {
    let toUpdate = []
    let old = state.currentSheet.rows[payload.rowNumber].groups[0][0].valid_selection

    state.currentSheet.rows[payload.rowNumber].groups.forEach(group => {
      group.forEach(digit => {
        toUpdate.push(digit)
        digit.valid_selection = !old
      })
    })
    payload.callback(toUpdate)
  },
  seending: (state, payload) => {
    state.currentSheet.seending = {
      ...state.currentSheet.seending,
      ...payload
    }
  },
  alterationDigit: (state, payload) => {
    let row = payload.position[0]
    let d = payload.position[1]

    const parseGroup = {'0': 0, '1': 0, '2': 0, '3': 0, '4': 1, '5': 1, '6': 1, '7': 1, '8': 2, '9': 2, '10': 2, '11': 2, '12': 3, '13': 3, '14': 3, '15': 3}
    const parseDigit = {'0': 0, '1': 1, '2': 2, '3': 3, '4': 0, '5': 1, '6': 2, '7': 3, '8': 0, '9': 1, '10': 2, '11': 3, '12': 0, '13': 1, '14': 2, '15': 3}

    const oldDigitHeuristic = state.currentSheet.rows[row].groups[parseGroup[d.toString()]][parseDigit[d.toString()]].heuristic
    Vue.set(state.currentSheet.rows[row].groups[parseGroup[d.toString()]], parseDigit[d.toString()], {
      ...payload.digit,
      heuristic: oldDigitHeuristic
    })
  },
  startEditing: (state, payload) => {
    state.editingSheet = payload
  },
  ignoreSheet: (state, payload) => {
    state.currentSheet = {}
    state.editingSheet = false
    router.push({name: 'sheetCheckerSelect'})
  },
  seendingSheet: (state, payload) => {
    state.seendingSheet = payload
  },
  resetQueue: (state) => {
    state.queueSheets = {actual: 0, total: 0, sheets: []}
  },
  verifyQueue: (state, payload) => {
    state.queueSheets = {actual: 0, total: payload.length, sheets: payload}
  },
  nextSheet: (state) => {
    state.queueSheets.actual = state.queueSheets.actual + 1
    state.editingSheet = false

    if (state.queueSheets.actual < state.queueSheets.total) {
      router.push({
        name: 'sheetChecker',
        params: {
          uuid: state.queueSheets.sheets[state.queueSheets.actual].uuid
        }
      })
    } else {
      state.queueSheets = {actual: 0, total: 0, sheets: []}
      router.push({name: 'sheetCheckerSelect'})
    }
  },
  shiftPressed: (state, payload) => {
    state.shiftPressed = payload
  },
  ctrlPressed: (state, payload) => {
    state.ctrlPressed = payload
  },
  autoCompleting: (state, payload) => { state.autoCompleting = payload },
  cachedSheets (state, paylaod) {
    state.cachedSheets = {...state.cachedSheets, ...paylaod}
  },
  processDjs (state, payload) {
    // ADD a journey folga to the days that are without any pts
    const folga = state.journeys.filter(i => i.name.toLowerCase() === 'folga')[0]

    const namesBlocked = ['férias', 'suspensão', 'dispensa']
    const blocked = [
      folga.id,
      ...state.journeys
        .filter(i => namesBlocked.includes(i.name.toLowerCase()))
        .map(i => i.id)
    ]

    const plane = state.currentSheet.rows.map(r => r.groups.reduce((a, b) => [...a, ...b]))

    payload.forEach((line, rowNumber) => {
      Vue.set(state.currentSheet.rows[rowNumber], 'dayJourney', line.dayJourney)
      Vue.set(state.currentSheet.rows[rowNumber], 'infoToRefetch', line.infoToRefetch)

      const currentLine = state.currentSheet.rows[rowNumber]

      if (plane[rowNumber].filter(digit => digit.machine_predicted === 'E' && !digit.user_predicted).length > 13 &&
        (currentLine.dayJourney.journey && !blocked.includes(currentLine.dayJourney.journey.id))) {
        Vue.set(state.currentSheet.rows[rowNumber], 'dayJourney', {
          ...state.currentSheet.rows[rowNumber].dayJourney,
          journey: folga,
          manual_change: true,
          guessFolga: true,
          _journeyChanged: true
        })
      }
    })
  },
  updateRowDayjourney (state, {rowNumber, dayJourney}) {
    Vue.set(state.currentSheet.rows[rowNumber], 'dayJourney', dayJourney)
  },
  processing (state, {types, add = false, remove = false}) {
    if (!add && !remove) {
      throw new Error(`the 'processing' action of sheet dont received add or remove params`)
    } else if ((add && add.length === 0) || (remove && remove.length === 0)) {
      return
    }
    types.forEach(type => {
      if (add) {
        state.processing[type] = Array.from(new Set([...state.processing[type], ...add]))
      } else {
        state.processing[type] = state.processing[type].filter(el => remove.indexOf(el) < 0)
      }
    })
  },
  updateRowsDayJourneys (state, payload) {
    payload.forEach(item => {
      for (let i = 0; i < state.currentSheet.rows.length; i++) {
        if (item.infoToRefetch.hashFront === state.currentSheet.rows[i].infoToRefetch.hashFront) {
          Vue.set(state.currentSheet.rows[i], 'dayJourney', item.dayJourney)
        }
      }
    })
  }
}

const actions = {
  autoCompleting: ({ commit }, payload) => { commit('autoCompleting', payload) },
  altered: ({ commit }, payload) => {
    payload.digit.verify = false
    delete payload.digit.animBlur
    commit('seending', {[payload.digit.id]: true})
    commit('alterationDigit', payload)

    function commitResponse (response) {
      commit('alterationDigit', {
        digit: {...response.data, animBlur: true},
        position: payload.position
      })
      commit('seending', {[payload.digit.id]: false})
    }

    for (let i = 0; i < 2; i++) {
      handleRequest.sendDigit(payload.digit.id, payload.digit)
        .then((response) => {
          commitResponse(response)
        }).catch(() => {
          if (i === 1) {
            commit('seending', {[payload.digit.id]: false})
            swal({
              title: 'Aconteceu um erro',
              text: 'Não foi possível salvar um dos dígitos, confira sua conexão com a internet. Se o erro persistir reprocesse a folha.',
              type: 'error'
            })
          }
        })
    }
  },
  async startVerify ({ commit, dispatch, state }, payload) {
    function processSheet (response) {
      let numRequiredFields = 0
      // verify it there's all necessary fields
      const requiredFields = ['id', 'start_date', 'rows', 'pdfUrl']
      requiredFields.forEach(field => {
        if (response.data[field] !== null && response.data[field] !== undefined) {
          numRequiredFields++
        }
      })

      let checkedDigits = 0
      let missingValuesDigits = 0
      if (response.data.rows) {
        response.data.rows.forEach(row => {
          row.groups.forEach(group => {
            group.forEach(digit => {
              checkedDigits++
              if (((digit.heuristic === '' || !digit.heuristic) && (!digit.machine_predicted || digit.machine_predicted === '')) &&
                !digit.user_predicted) {
                console.log(digit.heuristic, digit.machine_predicted)
                missingValuesDigits++
              }
            })
          })
        })
      }

      let error
      if (numRequiredFields !== requiredFields.length) {
        error = { title: 'Aconteceu um erro', text: 'Não foi possivel carregar as infromações da folha, tente novamente.' }
      } else if (checkedDigits === missingValuesDigits) {
        error = {
          title: 'Esta folha parece não ter sido escaneada corretamente.',
          text: 'Será necessario escanear novamente a folha do ' +
          'funcionário ' + response.data.employee_name + ' referente a ' + response.data.start_date
        }
      }
      if (!error) {
        commit('startVerify', response.data)
        dispatch('autoComplete/calcInitialAutoComplete', response.data)
        dispatch('processJourneys')
      } else {
        swal({
          ...error,
          type: 'error'
        })
        router.push({name: 'sheetCheckerSelect'})
      }
    }

    if (state.cachedSheets[payload.uuid]) {
      processSheet(state.cachedSheets[payload.uuid])
    } else {
      try {
        const response = await handleRequest.digits(payload)
        processSheet(response)
      } catch (e) {
        swal({
          title: 'Aconteceu um erro',
          text: 'Não foi possivel carregar as informações da folha, tente novamente.',
          type: 'error'
        }).then(() => {
          router.push({name: 'sheetCheckerSelect'})
        })
      }
    }

    // pre-load next sheets
    const PRE_LOAD = 2
    const CURRENT = state.queueSheets.sheets.findIndex(i => i.uuid === payload.uuid)
    const START = CURRENT + 1

    for (let i = START; i < START + PRE_LOAD && i < state.queueSheets.sheets.length; i++) {
      const uuid = state.queueSheets.sheets[i].uuid
      if (!state.queueSheets[uuid]) {
        handleRequest.digits({uuid}).then(response => {
          let urlsImages = []
          response.data.rows.forEach((r) => {
            r.groups.forEach((group) => {
              group.forEach(digit => {
                if (digit.image_url) {
                  urlsImages.push(digit.image_url)
                }
              })
            })
          })
          const preloaded = preLoadImages(urlsImages)
          commit('cachedSheets', {[response.data.uuid]: {data: response.data, _preloadedImages: preloaded}})
        })
      }
    }
  },
  sendSheet: async ({ commit, dispatch }, payload) => {
    let data = []
    commit('seendingSheet', true)

    await dispatch('saveJourneysChanges')

    state.currentSheet.rows.forEach(row => {
      row.groups.forEach(group => {
        group.forEach(digit => {
          data.push(digit)
        })
      })
    })
    handleRequest.apiPost('sheet-digits/' + state.currentSheet.id, data)
      .then(() => {
        commit('seendingSheet', false)
        swal({
          title: 'Folha aprovada com sucesso',
          type: 'success'
        }).then(() => {
          dispatch('nextSheet')
        }).catch(() => {
          dispatch('nextSheet')
        })
      })
      .catch((err) => {
        commit('seendingSheet', false)

        let msgError = 'Não foi possivel salvar as informações da folha, tente novamente.'
        if (err.response.data && err.response.data.error) {
          msgError = err.response.data.error
        }

        swal({
          title: 'Aconteceu um erro',
          text: msgError,
          type: 'error'
        })
      })
  },
  cutError: ({ commit }, payload) => {
    commit('cutError', {
      rowNumber: payload,
      callback: (toUpdate) => {
        toUpdate.forEach(digit => {
          commit('seending', {[digit.id]: true})
          handleRequest.sendDigit(digit.id, digit).then(() => {
            commit('seending', {[digit.id]: false})
          })
        })
      }
    })
  },
  editing: ({ commit }, payload) => {
    commit('startEditing', payload)
  },
  ignoreSheet: ({ commit }) => {
    const data = {
      to_ignore: true
    }
    commit('seendingSheet', true)
    handleRequest.api('sheet/' + state.currentSheet.id, data)
      .then(() => {
        commit('seendingSheet', false)
        swal({
          title: 'Folha ignorada com sucesso',
          type: 'success'
        }).then(() => {
          router.push({name: 'sheetCheckerSelect'})
        })
      })
    commit('ignoreSheet')
  },
  async verifyQueue ({ commit }, payload) {
    commit('verifyQueue', payload)
    router.push({
      name: 'sheetChecker',
      params: {
        uuid: payload[0].uuid
      }
    })
  },
  nextSheet: ({ commit }) => {
    commit('nextSheet')
  },
  clearAllValuesRow: async ({ commit, state }, payload) => {
    const allDigitsIdsRow = getters.currentSheetOrderedDigits(state)[payload]

    let toSend = []
    for (let i = 0; i < getters.currentSheetOrderedDigits(state).length; i++) {
      toSend[i] = [null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null]
    }
    toSend[payload] = allDigitsIdsRow

    allDigitsIdsRow.forEach(id => {
      commit('seending', {[id]: true})
    })

    const result = await handleRequest.autoFill({
      id: getters.currentSheetId(state),
      digits: toSend,
      times: [null, null, null, null]
    })
    commit('autoFillRows', result.data.success)
    allDigitsIdsRow.forEach(id => {
      commit('seending', {[id]: false})
    })
  },
  async processJourneys ({ commit, state }) {
    if (!state.journeys.length) {
      const journeys = await API.journey.get()
      commit('journeys', journeys.body.success)
    }
    const result = await API.partialTimesheet.save({
      startDate: moment(state.currentSheet.start_date, 'YYYY-MM-DD').toISOString(),
      endDate: moment(state.currentSheet.end_date, 'YYYY-MM-DD').toISOString(),
      employees: [state.currentSheet.employee_id],
      toLoad: ['djs']
    })
    commit('processDjs', result.data.success.lines)
  },
  async saveJourneysChanges ({ state }) {
    let linesJourneyChanged = state.currentSheet.rows.filter(i => i.dayJourney._journeyChanged)

    if (!linesJourneyChanged.length) { return }

    let data = {newDjJourneys: []} // {groups: [], journey}

    const grouped = groupBy(linesJourneyChanged, 'dayJourney.journey.id')

    const journeysPerId = {}
    state.currentSheet.rows.forEach(r => {
      journeysPerId[r.dayJourney.journey.id] = r.dayJourney.journey
    })

    grouped.forEach(item => {
      const lines = item.slice(1)[0]

      let group = {groups: [], journey: journeysPerId[item[0]]}

      lines.forEach(l => {
        group.groups.push({
          employee: state.currentSheet.employee_id,
          company: null,
          day: l.dayJourney.day
        })
      })
      data.newDjJourneys.push(group)
    })

    await API.changeMultipleDayjourney.save(data)
  },
  async changeJourneysLocal ({ commit, state, dispatch }, {rowNumber, journey}) {
    // It replaces the journey of each row to be the selected
    // IT IS NOT SAVED IN BACK YET, IT ONLY IS WHEN YOU APROVE

    let linesIndexes = [...state.selectedLines, rowNumber]

    commit('processing', {types: ['djs'], add: linesIndexes})

    let data = {newDjJourneys: [{groups: [], journey}]}

    linesIndexes.forEach(l => {
      const item = {
        employee: state.currentSheet.employee_id,
        company: null,
        day: state.currentSheet.rows[l].dayJourney.day
      }
      data.newDjJourneys[0].groups.push(item)
    })

    const linesToChange = linesIndexes.map(l => state.currentSheet.rows[l].infoToRefetch.hashFront)

    let fakeUpdate = _.clone(
      state.currentSheet.rows.filter(i => linesToChange.includes(i.infoToRefetch.hashFront))
    ).map(i => ({infoToRefetch: i.infoToRefetch, dayJourney: {...i.dayJourney, journey, _journeyChanged: true, manual_change: true}}))

    commit('selectedLines', [])
    commit('journeySearch', '')
    commit('updateRowsDayJourneys', fakeUpdate)
    commit('processing', {types: ['djs'], remove: linesIndexes})
  },
  autoCompleteDayJourneys ({ commit, state }, journey) {
    const linesIndexes = state.selectedRowsAutoFill

    const linesToChange = linesIndexes.map(l => state.currentSheet.rows[l].infoToRefetch.hashFront)

    commit('processing', {types: ['djs'], add: linesIndexes})

    let fakeUpdate = _.clone(
      state.currentSheet.rows.filter(i => linesToChange.includes(i.infoToRefetch.hashFront))
    ).map(i => ({infoToRefetch: i.infoToRefetch, dayJourney: {...i.dayJourney, journey, _journeyChanged: true, manual_change: true}}))
    commit('journeySearch', '')
    commit('updateRowsDayJourneys', fakeUpdate)
    commit('processing', {types: ['djs'], remove: linesIndexes})
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
  modules: {
    autoComplete
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/Sheet.js